import {
  Processor as SchemaProcessor,
  StripePayout,
  JustifiPayout,
  JustifiBankAccount,
} from '@weave/schema-gen-ts/dist/schemas/payments-platform/payouts/v1';
import { PayoutDestination } from '@weave/schema-gen-ts/dist/schemas/payments-platform/stripe-integration';
import { PayoutStatus } from '@frontend/api-payouts';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { useMerchants } from '@frontend/payments-hooks';
import { useAppScopeStore } from '@frontend/scope';
import { useMatch } from '@frontend/weave-router';
import { Breadcrumb, ContentLoader } from '@frontend/design-system';
import { useGetPayoutV1Query } from '../../hooks/use-get-payout';
import { PaymentError } from '../PaymentError';
import { PayoutAccountDetailsCard } from './PayoutAccountDetailsCard';
import { PayoutSummaryCard } from './PayoutSummaryCard';
import { JustifiPayoutSummaryTable, StripePayoutSummaryTable } from './PayoutSummaryTable';
import { JustifiPayoutTransactionsTable, StripePayoutTransactionsTable } from './PayoutTransactionsTable';

const PayoutBreadcrumbs = () => {
  const { t } = useTranslation('payments');

  return (
    <Breadcrumb>
      <Breadcrumb.Path to='/payments/payouts' label={t('Payouts List')} />
      <Breadcrumb.Path label={t('Details')} />
    </Breadcrumb>
  );
};

function getPayoutStatusFromString(statusString: string): PayoutStatus | undefined {
  if (typeof statusString !== 'string' || statusString.trim() === '') {
    return undefined;
  }

  const normalizedStatus = statusString.toUpperCase(); // Normalize to uppercase like enum values
  // Check if the normalized string is one of the values in the PayoutStatus enum
  if (Object.values(PayoutStatus).includes(normalizedStatus as PayoutStatus)) {
    // If it is, we can safely cast it to PayoutStatus
    return normalizedStatus as PayoutStatus;
  }

  return undefined; // Return undefined if no match is found
}

export const PayoutDetails = ({
  payoutId,
  payoutLocationId,
}: {
  payoutId: string;
  payoutLocationId: string | null;
}) => {
  const isPayoutDetailsPage = !!useMatch({ from: '/payments/payouts_/$payoutId_/$locationId', shouldThrow: false });
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { activeProcessor, locationId } = useMerchants(locationIds)['firstLocation'];
  const processor = activeProcessor?.processorType === 'STRIPE' ? SchemaProcessor.STRIPE : SchemaProcessor.JUSTIFI;
  const finalLocationId = payoutLocationId || locationId || '';
  const { data, isLoading, error } = useGetPayoutV1Query({
    payoutId,
    locationId: finalLocationId,
    processor,
  });

  const stripePayout: StripePayout | undefined =
    data && 'stripePayout' in data && data.stripePayout ? data.stripePayout : undefined;
  const justifiPayout: JustifiPayout | undefined =
    data && 'justifiPayout' in data && data.justifiPayout ? data.justifiPayout : undefined;

  return error ? (
    <PaymentError />
  ) : (
    <>
      <ContentLoader show={isLoading} />
      {!isLoading && (
        <>
          {isPayoutDetailsPage && <PayoutBreadcrumbs />}
          {stripePayout && (
            <>
              <StripePayoutSummaryCard stripePayout={stripePayout} />
              <StripePayoutAccountDetailsCard destination={stripePayout.destination || {}} />
              <StripePayoutSummaryTable payoutId={stripePayout.id || ''} locationId={finalLocationId} />
              <StripePayoutTransactionsTable payoutId={stripePayout.id || ''} locationId={finalLocationId} />
            </>
          )}
          {justifiPayout && (
            <>
              <JustifiPayoutSummaryCard justifiPayout={justifiPayout} />
              <JustifiPayoutAccountDetailsCard bankAccount={justifiPayout.destination || {}} />
              <JustifiPayoutSummaryTable payoutId={justifiPayout.id || ''} locationId={finalLocationId} />
              <JustifiPayoutTransactionsTable payoutId={justifiPayout.id || ''} locationId={finalLocationId} />
            </>
          )}
        </>
      )}
    </>
  );
};

const JustifiPayoutAccountDetailsCard = ({ bankAccount }: { bankAccount: JustifiBankAccount }) => {
  return (
    <PayoutAccountDetailsCard
      loading={false}
      destination={{
        accountName: bankAccount?.bankName || '',
        brand: '',
        country: bankAccount?.country || '',
        currency: bankAccount?.currency || '',
        destinationType: bankAccount?.accountType || '',
        fingerprint: '',
        last4: bankAccount?.accountNumberLast4 || '',
        routingNumber: bankAccount?.routingNumber || '',
        id: bankAccount?.id || '',
      }}
    />
  );
};

const StripePayoutAccountDetailsCard = ({ destination }: { destination: PayoutDestination }) => {
  return (
    <PayoutAccountDetailsCard
      loading={false}
      destination={{
        accountName: destination?.bankAccount?.bankName || '',
        brand: destination?.card?.brand || '',
        country: destination?.bankAccount?.country || '',
        currency: destination?.bankAccount?.currency || '',
        destinationType: destination?.type || '',
        fingerprint: destination?.bankAccount?.fingerprint || '',
        last4: destination?.bankAccount?.last4 || '',
        routingNumber: destination?.bankAccount?.routingNumber || '',
        id: destination?.id || '',
      }}
    />
  );
};

const StripePayoutSummaryCard = ({ stripePayout }: { stripePayout: StripePayout }) => (
  <PayoutSummaryCard
    payout={{
      amount: stripePayout?.amount || 0,
      currency: stripePayout?.currency?.toUpperCase() || '',
      status: getPayoutStatusFromString(stripePayout?.status || '') || '',
      estimatedArrivalOn: formatDate(Number(stripePayout?.arrivalDate), 'MMM D, YYYY', true) || '',
      payoutId: stripePayout?.id || '',
      descriptor: stripePayout?.statementDescriptor || '',
    }}
  />
);

const JustifiPayoutSummaryCard = ({ justifiPayout }: { justifiPayout: JustifiPayout }) => (
  <PayoutSummaryCard
    payout={{
      amount: justifiPayout?.amount || 0,
      currency: justifiPayout?.currency || '',
      status: getPayoutStatusFromString(justifiPayout?.status || '') || '',
      estimatedArrivalOn: formatDate(justifiPayout?.depositsAt, 'MMM D, YYYY', true) || '',
      payoutId: justifiPayout?.id || '',
      descriptor: justifiPayout?.description || '',
    }}
  />
);
